import * as React from "react"
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  TextField,
  Button,
  Stack,
  Divider,
  Box,
  LinearProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  CardActions,
} from "@mui/material"
import SaveIcon from "@mui/icons-material/Save"
import DeleteIcon from "@mui/icons-material/Delete"
import { api } from "../../function/axios"

type PromptItem = {
  id?: number
  name: string
  updatedAt?: string
  text: string
}

type CountryOption = {
  code: string
  label: string
}

const COUNTRIES: CountryOption[] = [
  { code: "US", label: "United States" },
  { code: "IL", label: "Israel" },
]

const promptNameFromCountry = (code: string) => `${code}`

const countryFromPromptName = (name: string) => {
  const parts = String(name || "").split("_")
  const code = parts[parts.length - 1] || ""
  return code
}

export default function PromptEditorMui() {
  const [items, setItems] = React.useState<PromptItem[]>([])
  const [selected, setSelected] = React.useState<string>("") // countryCode
  const [loading, setLoading] = React.useState<boolean>(true)
  const [saving, setSaving] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)
  const [info, setInfo] = React.useState<string | null>(null)

  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [deleteTarget, setDeleteTarget] = React.useState<PromptItem | null>(null)

  const current = React.useMemo(() => {
    if (!selected) return undefined
    return items.find((i) => i.name === promptNameFromCountry(selected))
  }, [items, selected])

  const refetchPrompts = React.useCallback(async () => {
    const res = await api({ method: "GET", url: "/admin_settings/pdf_report" })
    if (!res.code) throw new Error(`GET prompts → ${res.code}`)

    const data: Array<{
      id?: number
      name: string
      content?: string
      updated_at?: string
    }> = res?.data

    const mapped: PromptItem[] = (Array.isArray(data) ? data : []).map((d, idx) => ({
      id: Number.isFinite(Number(d.id)) ? Number(d.id) : idx + 1,
      name: d.name,
      updatedAt: d.updated_at ? `Updated ${new Date(d.updated_at).toLocaleString()}` : undefined,
      text: d.content ?? "",
    }))

    setItems(mapped)

    if (!selected && mapped.length) {
      const code = countryFromPromptName(mapped[0].name)
      if (code) setSelected(code)
    }
  }, [selected])

  React.useEffect(() => {
  if (!selected) return

  const name = promptNameFromCountry(selected)
  const exists = items.some(i => i.name === name)

  if (!exists) {
    setItems(prev => [
      ...prev,
      {
        id: undefined,
        name,
        updatedAt: undefined,
        text: "",
      },
    ])
  }
}, [selected, items])
  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      setSaving(true)
      setError(null)

      await api({
        method: "DELETE",
        url: `/admin_settings/pdf_report/${deleteTarget.id}`,
      })

      const deletedCode = countryFromPromptName(deleteTarget.name)
      if (selected === deletedCode) setSelected("")

      await refetchPrompts()
      setInfo(`Prompt removed: ${deleteTarget.name}`)
    } catch (err: any) {
      setError(err.message ?? String(err))
    } finally {
      setSaving(false)
      setConfirmOpen(false)
      setDeleteTarget(null)
    }
  }

  React.useEffect(() => {
    let cancelled = false
      ; (async () => {
        setLoading(true)
        setError(null)
        try {
          await refetchPrompts()
        } catch (e: any) {
          if (!cancelled) setError(e.message ?? String(e))
        } finally {
          if (!cancelled) setLoading(false)
        }
      })()
    return () => {
      cancelled = true
    }
  }, [refetchPrompts])

  const updateText = (value: string) => {
    if (!selected) return
    const promptName = promptNameFromCountry(selected)
    setItems((prev) =>
      prev.map((i) => (i.name === promptName ? { ...i, text: value } : i)),
    )
  }

  const putPrompt = async (setActive: boolean) => {
    if (!selected) return

    setSaving(true)
    setError(null)
    setInfo(null)

    try {
      const promptName = promptNameFromCountry(selected)
      const content = current?.text ?? ""

      const payload = {
        id: current?.id,
        name: promptName,
        content: current?.text,
        setActive,
      }

      const res = await api({
        method: "POST",
        url: "/admin_settings/pdf_report",
        headers: { "Content-Type": "application/json" },
        body: payload,
      })
      if (!res.code) throw new Error(`PUT prompt → ${res.code}`)

      await refetchPrompts()
      setInfo(setActive ? `Prompt saved and set as active` : `Prompt saved: ${promptName}`)
    } catch (e: any) {
      setError(e.message ?? String(e))
    } finally {
      setSaving(false)
    }
  }

  const handleSave = () => void putPrompt(false)

  const existingCodes = React.useMemo(() => {
    const s = new Set<string>()
    for (const it of items) {
      const code = countryFromPromptName(it.name)
      if (code) s.add(code)
    }
    return s
  }, [items])

  const isIL = selected === "IL"

return (
  <Box sx={{ height: "100%" }}>
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        width: "100%",
        height: "100%",
        borderRadius: 2,
        boxShadow: 0,
        borderColor: (theme) =>
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "divider",
      }}
    >
      <CardHeader
        title={
          <Typography variant="subtitle1" fontWeight={600}>
            Prompts
          </Typography>
        }
        subheader={
          <Typography variant="caption" color="text.secondary">
            Edit, rename, and activate a system prompt
          </Typography>
        }
        sx={{ pb: 0.5 }}
      />

      {(loading || saving) && <LinearProgress />}
      <Divider />

      <CardContent
        sx={{
          pt: 2,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          <Grid item xs={12} md={4} lg={3} sx={{ minHeight: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              Prompt library
            </Typography>

            <List
              disablePadding
              sx={{
                bgcolor: "action.hover",
                borderRadius: 2,
                p: 1,
                maxHeight: 400,
                overflowY: "auto",
              }}
            >
              {items.map((p) => {
                const code = countryFromPromptName(p.name)
                const label =
                  COUNTRIES.find((c) => c.code === code)?.label || p.name

                return (
                  <Box
                    key={p.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <ListItemButton
                      selected={selected === code}
                      onClick={() => setSelected(code)}
                      sx={{
                        flex: 1,
                        borderRadius: 2,
                        alignItems: "flex-start",
                      }}
                      disabled={loading || saving}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Tooltip
                              title={label.length > 20 ? label : ""}
                              arrow
                              placement="top"
                            >
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                                sx={{ maxWidth: 140 }}
                              >
                                {label.length > 20
                                  ? `${label.slice(0, 20)}…`
                                  : label}
                              </Typography>
                            </Tooltip>

                            {code ? <Chip size="small" label={code} /> : null}
                          </Stack>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.6rem" }}
                          >
                            {p.updatedAt || ""}
                          </Typography>
                        }
                      />
                    </ListItemButton>

                    <Button
                      variant="contained"
                      color="error"
                      disabled={loading || saving}
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeleteTarget(p)
                        setConfirmOpen(true)
                      }}
                      sx={{
                        width: 27,
                        height: 27,
                        minWidth: 0,
                        borderRadius: "50%",
                        p: 0,
                      }}
                    >
                      <DeleteIcon sx={{ width: 20, height: 20 }} />
                    </Button>
                  </Box>
                )
              })}
            </List>
          </Grid>

          <Grid item xs={12} md={8} lg={9} sx={{ minHeight: 0 }}>
            <Stack spacing={2} sx={{ height: "100%", minHeight: 0 }}>
              <TextField
                select
                label="Country"
                variant="outlined"
                value={selected}
                onChange={(e) => setSelected(String(e.target.value))}
                fullWidth
                disabled={loading || saving}
                SelectProps={{
                  MenuProps: {
                    disablePortal: true,
                    disableScrollLock: true,
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select country</em>
                </MenuItem>

                {COUNTRIES.map((c) => {
                  const alreadyExists = existingCodes.has(c.code)
                  const disabled = alreadyExists && c.code !== selected

                  return (
                    <MenuItem key={c.code} value={c.code} disabled={disabled}>
                      {c.label} ({c.code}){" "}
                      {alreadyExists && c.code !== selected
                        ? "— already exists"
                        : ""}
                    </MenuItem>
                  )
                })}
              </TextField>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="textarea"
                  value={current?.text ?? ""}
                  onChange={(e: any) => updateText(e.target.value)}
                  disabled={!selected || loading || saving}
                  placeholder="System prompt"
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    outline: "none",
                    padding: "16px",
                    resize: "none",
                    background: "transparent",
                    font: "inherit",
                    boxSizing: "border-box",
                    overflowY: "auto",
                    direction: isIL ? "rtl" : "ltr",
                    textAlign: isIL ? "right" : "left",
                  }}
                />
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>

      <Divider />

      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Selected:{" "}
          <Box component="span" sx={{ color: "text.primary" }}>
            {selected || "—"}
          </Box>
        </Typography>

        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!selected || loading || saving}
          sx={{ textTransform: "none" }}
        >
          Save
        </Button>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false)
          setDeleteTarget(null)
        }}
      >
        <DialogTitle>Delete</DialogTitle>

        <DialogContent>
          <Typography variant="body2">
            Delete <b>{deleteTarget?.name}</b> prompt?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOpen(false)
              setDeleteTarget(null)
            }}
          >
            No
          </Button>

          <Button color="error" onClick={handleDelete} disabled={saving}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  </Box>
)
}
